import { Injectable, SecurityContext, WritableSignal, signal } from '@angular/core';
import { Coordinates, Feature } from '@api/models';
import { MapService } from '@features/planification-map/components/map/services/map.service';
import { SelectRoutePlacesService } from '@features/planification-map/components/route-form/components/select-route-form/services/select-route-places.service';
import { Itinerary, RouteFormResult, RouteRequestParams } from '@features/planification-map/components/route-form/models/routeRequestResults.model';
import { ReverseGeocodingService } from '../../api/services/reverse-geocoding.service';
import { map, switchMap, tap, filter, of, BehaviorSubject, take, from } from 'rxjs';
import { RouteFormService } from '@features/planification-map/components/route-form/services';
import { UtilsService } from '@shared/services/utils.service';
import { itineraryDetailsMode } from '@features/planification-map/components/map/components/map-results-footer/models/itinerary-details-mode.model';
import { allOption, bikeOption, busOption, walkOption } from '@features/planification-map/components/route-form/components/route-form-options-panels/components/route-form-more-options/constants';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { RouteFormDateSelectorService } from '@features/planification-map';
import { RouteFormMoreOptionsService } from '@features/planification-map/components/route-form/components/route-form-options-panels/components/route-form-more-options/services/route-form-more-options.service';

@Injectable({
  providedIn: 'root'
})
export class CoreService {


  public sidenavOpened = signal(!this.utils.isMobile());

  public selectedFromOption:WritableSignal<Feature|null> = this.selectRoutePlacesService.selectedFromOption;
  public selectedToOption:WritableSignal<Feature|null> = this.selectRoutePlacesService.selectedToOption;

  public itineraryIndexSelected:WritableSignal<number> = signal(-1);
  public itinerarySelected:WritableSignal<Itinerary|null> = signal(null);

  public showItineraryDetails:WritableSignal<itineraryDetailsMode> = signal('midOpen');

  public routeFormResult:WritableSignal<RouteFormResult|null> = signal(null);

  public loadingRouteResults = new BehaviorSubject(false);

  constructor(private mapService: MapService,
              private selectRoutePlacesService: SelectRoutePlacesService,
              private reverseGeocodingService: ReverseGeocodingService,
              private routeFormService: RouteFormService,
              private routeFormDateSelectorService: RouteFormDateSelectorService,
              private routeFormMoreOptionsService: RouteFormMoreOptionsService,
              private router: Router,
              private utils: UtilsService, private matIconRegistry: MatIconRegistry, private sanitizer: DomSanitizer) {}

  public cleanFormConfigurationFields() {
    this.selectedFromOption.set(null);
    this.selectedToOption.set(null);
    this.routeFormDateSelectorService.resetDates();
    this.routeFormMoreOptionsService.resetOptions();
  } 

  public setLoadingRouteResults(value: boolean) {
    this.loadingRouteResults.next(value);
  }
  public getCoordinatesFromClickMap( signalToUpdate:WritableSignal<Feature|null>) {
    this.mapService.waitingForClick.set(true);
    this.sidenavOpened.set(false);
    return this.fillRouteOptionWithPointClickedOnMap(signalToUpdate).pipe(
      tap(() => {
        this.mapService.waitingForClick.set(false);
        this.sidenavOpened.set(true);
      })
    )
  }

  private fillRouteOptionWithPointClickedOnMap(signalToUpdate:WritableSignal<Feature|null>) {
    return from(this.mapService.waitForClickToGetCoordinates()).pipe(
      switchMap((clickData: any) => this.reverseGeocodingService.reverseGeocoding({lat: clickData.latitude,lng: clickData.longitude})),
      map((response) => response.features[0]),
      tap((response) => {
         if(!response) { this.utils.showSnackBarError('ubicationOutOfBounds') }
         else {
            signalToUpdate.set(response);
         }
      })
    )
  }

  public selectItinerary(itinerary: Itinerary|null, closeSidenav = true) {
    this.mapService.clearRouteGraphics();
    if(!itinerary) {
      this.restoreMapToDontShowResults();
      return;
    }
    this.mapService.hideAllLayers();
    this.mapService.drawItinerary(itinerary);
    this.mapService.showingResults.set(true)
    if(closeSidenav && this.utils.isMobile()) this.sidenavOpened.set(false);
  }

  public restoreMapToDontShowResults() {
    this.mapService.showAllLayers();
    this.mapService.showingResults.set(false)
  }

  public cleanAllItinerariesSearch() {
    if(this.mapService.showingResults()) {
      this.selectItinerary(null);
    }
    this.routeFormResult.set(null);
    this.itineraryIndexSelected.set(-1);
  }

  public autocompleteFromWithCurrentUbication() {
    if(this.selectedFromOption()) {
      this.doSearchRequest().pipe(
        tap((response) => {
          this.routeFormResult.set(response);
          this.sidenavOpened.set(true);
          this.loadingRouteResults.next(false)
        })
      ).subscribe();
    } else {
      const coordinates = this.mapService.getCurrentCoordinates();
      if(!coordinates){
         this.sidenavOpened.set(true);
         return;
      }
      this.createRouteFromLatitudeLongitude(coordinates.lat,coordinates.lng, () => {
        this.sidenavOpened.set(true);
      })

    }
  }


  public createRouteFromLatitudeLongitude(lat: number, lng: number, callback?: () => void) {
    this.fillRouteOptionWithLatLon(lat,lng,this.selectedFromOption, undefined, callback).pipe(
      filter(() => !!this.selectedToOption() && !!this.selectedFromOption()),
      switchMap(() => this.doSearchRequest()),
      tap((response) => {
        this.routeFormResult.set(response);
        this.sidenavOpened.set(true);
        this.loadingRouteResults.next(false)
      })
    )
    .subscribe();
  }

  public createRouteFromClickOnMap() {
    this.mapService.waitingForClick.set(true);
    this.sidenavOpened.set(false);
    this.fillRouteOptionWithPointClickedOnMap(this.selectedToOption).pipe(
      tap(() => {
        if (!this.selectedToOption()) {
          this.mapService.waitingForClick.set(false);
        }
      }),
      filter(() => !!this.selectedToOption()),
      switchMap(() => {
        if(this.selectedFromOption()) {
          return of(this.selectedFromOption());
        }
        const coordinates = this.mapService.getCurrentCoordinates();
        if(!coordinates) {
          this.mapService.waitingForClick.set(false);
          this.sidenavOpened.set(true);
          return of(null);
        }
        return this.fillRouteOptionWithLatLon(coordinates!.lat,coordinates!.lng,this.selectedFromOption, 'canNotGetYourUbication', () => {
          this.mapService.waitingForClick.set(false);
          this.sidenavOpened.set(true);
        })
      }),
      filter(() => !!this.selectedFromOption()),
      switchMap(() => this.doSearchRequest()),
      tap((response) => {
        this.routeFormResult.set(response);
        this.mapService.waitingForClick.set(false);
        this.sidenavOpened.set(true);
        this.loadingRouteResults.next(false)
      })
    )
    .subscribe();
  }

  
  public createRouteToLatitudeLongitude(lat: number, lng: number, callback?: () => void) {
    let coordinates:Coordinates | null = null;
    if(!this.selectedFromOption()) {
      coordinates = this.mapService.getCurrentCoordinates();
    }
    this.fillRouteOptionWithLatLon(lat,lng,this.selectedToOption, undefined, callback).pipe(
      tap(() => {if (!coordinates && !this.selectedFromOption()) {
         this.sidenavOpened.set(true);
      }}),
      filter(() => !!coordinates || !!this.selectedFromOption()),
      switchMap(() => {
        if(this.selectedFromOption()) {
          return of(this.selectedFromOption());
        }
        return this.fillRouteOptionWithLatLon(coordinates!.lat,coordinates!.lng,this.selectedFromOption, 'canNotGetYourUbication')
      }),
      switchMap(() => this.doSearchRequest()),
      tap((response) => {
        this.routeFormResult.set(response);
        this.sidenavOpened.set(true);
        this.loadingRouteResults.next(false)
      })
    ).subscribe();
  }

  private fillRouteOptionWithLatLon(lat: number, lng: number, routeOption: WritableSignal<Feature|null>, errorMessage='ubicationOutOfBounds', errorCallback?: () => void) {
    return this.reverseGeocodingService.reverseGeocoding({lat,lng}).pipe(
      tap((result) => { if (!result?.features?.length) { 
        this.utils.showSnackBarError(errorMessage);
        if(errorCallback) { errorCallback() };
      }}),
      filter((response) => !!response && !!response.features && response.features.length > 0),
      map((response) => response.features[0]),
      tap((response) => { routeOption.set(response)})
    )
  }

  private doSearchRequest(params?: RouteRequestParams) {
    this.loadingRouteResults.next(true)
    return this.routeFormService.doSearchRequest(params).pipe(
      tap((result) =>{ if(!result) { this.loadingRouteResults.next(false) }}),
      filter((result) => !!result),
    )
  }

  public sanitizeIcons() {
    this.sanitizeIcon('assets/img/walk.svg', walkOption.id)
    this.sanitizeIcon('assets/img/bus.svg', busOption.id);
    this.sanitizeIcon('assets/img/bike.svg', bikeOption.id);
    this.sanitizeIcon('assets/img/all.svg', allOption.id);
    this.sanitizeIcon('assets/img/map_gallery.svg', "map_gallery");
    this.sanitizeIcon('assets/img/map_legend.svg', "map_legend");
    this.sanitizeIcon('assets/img/map-location-arrow.svg', "map_location_arrow");
    this.sanitizeIcon('assets/img/a_from.svg', "aFrom");
    this.sanitizeIcon('assets/img/b_to.svg', "bTo");
    this.sanitizeIcon('assets/img/mid-route-step.svg', "midStep");
    this.sanitizeIcon('assets/img/distance.svg', "distance");
  }

  private sanitizeIcon(iconSrc:string, id:string) {
    const url = this.sanitizer.sanitize(SecurityContext.URL, iconSrc);
    if(!url) { return; }
    const safeIcon = this.sanitizer.bypassSecurityTrustResourceUrl(url)
    this.matIconRegistry.addSvgIcon(id, safeIcon)
  }

  public shareRouteByUrl() {
    const routeParams = this.routeFormService.getLastRouteRequestParams();
    if (!routeParams) { return; }
    const encodedParams = this.utils.encodeToSafeBase64((JSON.stringify(routeParams)));
    const currentUrl = this.router.url.split('?')[0];
    const urlWithParams = `${currentUrl}?routeParams=${encodedParams}`;
    this.router.navigateByUrl(urlWithParams);
    this.utils.copyToClipboard(window.location.origin + urlWithParams);
  }

  public doRequestFromUrlParams(codifiedParams: string) {
    let decodedParams: RouteRequestParams|undefined;;
    try {
      decodedParams =  JSON.parse(this.utils.decodeFromSafeBase64(codifiedParams)) as RouteRequestParams;
    } catch (error) {
      this.utils.showSnackBarError('invalidUrl');
    }
    if(!decodedParams) { return; }
    const fromPlace = decodedParams.fromPlace.split(',');
    const toPlace = decodedParams.toPlace.split(',');
    const fromCoordinates = {lat: parseFloat(fromPlace[0]), lng: parseFloat(fromPlace[1])};
    const toCoordinates = {lat: parseFloat(toPlace[0]), lng: parseFloat(toPlace[1])};
    this.selectedFromOption.set(this.utils.createFeatureFromCoordinates(fromCoordinates, decodedParams.fromDescription as string));
    this.selectedToOption.set(this.utils.createFeatureFromCoordinates(toCoordinates, decodedParams.toDescription as string));
    this.doSearchRequest(decodedParams)
    .pipe(
      take(1),
      tap((response) => {
        this.routeFormService.setFormsWithUrlParams(decodedParams as RouteRequestParams);
        this.routeFormResult.set(response);
        this.sidenavOpened.set(true);
        this.loadingRouteResults.next(false)
      })
    ).subscribe();


  }
}